import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';

@observer
class WorkSharingItemDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            item: null,
            loading: true,
            newNote: '',
            outcome: '',
        };
    }

    async componentDidMount() {
        const { workSharingItemStore } = this.props.stores;
        const { id } = this.props;
        if (id) {
            const response = await workSharingItemStore.getItem(id);
            if (response && response.status === 200) {
                this.setState({ item: response.data, outcome: response.data?.outcome || '', loading: false });
            } else {
                this.setState({ loading: false });
            }
        }
    }

    formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleString('nb-NO', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    getStatusLabel = (status) => {
        const labels = { open: 'Ledig', in_progress: 'Under arbeid', completed: 'Fullført', cancelled: 'Kansellert' };
        return labels[status] || status;
    }

    getStatusBadgeClass = (status) => {
        const classes = { open: 'bg-primary', in_progress: 'bg-warning text-dark', completed: 'bg-success', cancelled: 'bg-secondary' };
        return classes[status] || 'bg-secondary';
    }

    getPriorityLabel = (priority) => {
        const labels = { low: 'Lav', normal: 'Normal', high: 'Høy', urgent: 'Haster' };
        return labels[priority] || priority;
    }

    getPriorityBadgeClass = (priority) => {
        const classes = { low: 'bg-secondary', normal: 'bg-primary', high: 'bg-warning text-dark', urgent: 'bg-danger' };
        return classes[priority] || 'bg-secondary';
    }

    getJobTypeLabel = (jobType) => {
        const labels = { consultation: 'Konsultasjon', vaccination: 'Vaksinering', emergency: 'Akutt', surgery: 'Kirurgi', checkup: 'Kontroll', other: 'Annet' };
        return labels[jobType] || jobType;
    }

    handleGrabItem = async () => {
        const { workSharingItemStore } = this.props.stores;
        const { item } = this.state;
        await workSharingItemStore.grabItem(item.id);
        const response = await workSharingItemStore.getItem(item.id);
        if (response?.status === 200) {
            this.setState({ item: response.data });
        }
        if (this.props.callback) this.props.callback();
    }

    handleReleaseItem = async () => {
        const { workSharingItemStore } = this.props.stores;
        const { item } = this.state;
        await workSharingItemStore.releaseItem(item.id);
        const response = await workSharingItemStore.getItem(item.id);
        if (response?.status === 200) {
            this.setState({ item: response.data });
        }
        if (this.props.callback) this.props.callback();
    }

    handleCompleteItem = async () => {
        const { workSharingItemStore } = this.props.stores;
        const { item, outcome } = this.state;
        await workSharingItemStore.completeItem(item.id, outcome);
        const response = await workSharingItemStore.getItem(item.id);
        if (response?.status === 200) {
            this.setState({ item: response.data });
        }
        if (this.props.callback) this.props.callback();
    }

    handleCancelItem = async () => {
        const { workSharingItemStore } = this.props.stores;
        const { item } = this.state;
        await workSharingItemStore.cancelItem(item.id);
        const response = await workSharingItemStore.getItem(item.id);
        if (response?.status === 200) {
            this.setState({ item: response.data });
        }
        if (this.props.callback) this.props.callback();
    }

    handleAddNote = async () => {
        const { workSharingItemStore } = this.props.stores;
        const { item, newNote } = this.state;
        if (!newNote.trim()) return;
        await workSharingItemStore.addNote(item.id, newNote);
        const response = await workSharingItemStore.getItem(item.id);
        if (response?.status === 200) {
            this.setState({ item: response.data, newNote: '' });
        }
    }

    handleClose = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    render() {
        const { item, loading, newNote, outcome } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (loading) {
            return (
                <div class='d-flex justify-content-center p-4'>
                    <div class='spinner-border text-primary' role='status' />
                </div>
            );
        }

        if (!item) {
            return <div class='p-4 text-body-secondary'>Jobb ikke funnet.</div>;
        }

        const isAssignedToMe = item.assignedTo === user.id;
        const canGrab = item.status === 'open' || (item.status === 'in_progress' && !item.assignedTo);
        const canRelease = isAssignedToMe && item.status === 'in_progress';
        const canComplete = item.status !== 'completed' && item.status !== 'cancelled';
        const canCancel = item.status !== 'completed' && item.status !== 'cancelled';

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    {/* Info card */}
                    <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Status</span>
                            <span class='ms-2'>
                                <span class={`badge ${this.getStatusBadgeClass(item.status)}`}>{this.getStatusLabel(item.status)}</span>
                            </span>
                        </div>
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Tittel</span>
                            <span class='ms-2'><strong>{item.title}</strong></span>
                        </div>
                        {item.description && (
                            <div class='d-flex flex-row'>
                                <span class='text-muted font-weight-light'>Beskrivelse</span>
                                <span class='ms-2' style='white-space: pre-wrap;'>{item.description}</span>
                            </div>
                        )}
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Kunde</span>
                            <span class='ms-2'><strong>{item.customerName}</strong></span>
                        </div>
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Prioritet</span>
                            <span class='ms-2'>
                                <span class={`badge ${this.getPriorityBadgeClass(item.priority)}`}>{this.getPriorityLabel(item.priority)}</span>
                            </span>
                        </div>
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Type</span>
                            <span class='ms-2'>{this.getJobTypeLabel(item.jobType)}</span>
                        </div>
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Opprettet av</span>
                            <span class='ms-2'>{item.createdByName || '-'}, {this.formatDate(item.createdDate)}</span>
                        </div>
                        <div class='d-flex flex-row'>
                            <span class='text-muted font-weight-light'>Tildelt til</span>
                            <span class='ms-2'>
                                {item.assignedTo ? <>{item.assignedToName}, {this.formatDate(item.assignedDate)}</> : 'Ikke tildelt'}
                            </span>
                        </div>
                        {(item.contactName || item.contactPhone) && (<>
                            <div class='d-flex flex-row'>
                                <span class='text-muted font-weight-light'>Kontaktperson</span>
                                <span class='ms-2'>{item.contactName || '-'}</span>
                            </div>
                            {item.contactPhone && (
                                <div class='d-flex flex-row'>
                                    <span class='text-muted font-weight-light'>Telefon</span>
                                    <span class='ms-2'><a href={`tel:${item.contactPhone}`}>{item.contactPhone}</a></span>
                                </div>
                            )}
                        </>)}
                    </div>

                    {/* Dyr and Eier cards */}
                    <div class='w-100 d-flex flex-sm-row flex-column mt-0'>
                        {item.animalData && (
                            <div class='w-100 d-flex flex-column'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mt-3'>
                                    <h4 style='padding-bottom: 5px;'>Dyr</h4>
                                </div>
                                <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                    <div class='d-flex flex-row'>
                                        <span class='text-muted font-weight-light'>Navn</span>
                                        <span class='ms-2'><strong>{item.animalData.name}</strong></span>
                                    </div>
                                    {item.animalData.species && (
                                        <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>Art</span>
                                            <span class='ms-2'>{item.animalData.species}</span>
                                        </div>
                                    )}
                                    {item.animalData.breed && (
                                        <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>Rase</span>
                                            <span class='ms-2'>{item.animalData.breed}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {item.visitorData && (
                            <div class='w-100 d-flex flex-column'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mt-3'>
                                    <h4 style='padding-bottom: 5px;'>Eier</h4>
                                </div>
                                <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                    <div class='d-flex flex-row'>
                                        <span class='text-muted font-weight-light'>Fornavn</span>
                                        <span class='ms-2'><strong>{item.visitorData.firstname}</strong></span>
                                    </div>
                                    <div class='d-flex flex-row'>
                                        <span class='text-muted font-weight-light'>Etternavn</span>
                                        <span class='ms-2'><strong>{item.visitorData.lastname}</strong></span>
                                    </div>
                                    {item.visitorData.cellphone && (
                                        <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>Mobiltelefon</span>
                                            <span class='ms-2'><a href={`tel:${item.visitorData.cellphone}`}>{item.visitorData.cellphone}</a></span>
                                        </div>
                                    )}
                                    {item.visitorData.email && (
                                        <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>E-post</span>
                                            <span class='ms-2'><a href={`mailto:${item.visitorData.email}`}>{item.visitorData.email}</a></span>
                                        </div>
                                    )}
                                    {item.visitorData.address && (
                                        <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>Adresse</span>
                                            <span class='ms-2'>{item.visitorData.address}</span>
                                        </div>
                                    )}
                                    {(item.visitorData.postalcode || item.visitorData.place) && (
                                        <div class='d-flex flex-row'>
                                            <span class='text-muted font-weight-light'>Sted</span>
                                            <span class='ms-2'>{item.visitorData.postalcode} {item.visitorData.place}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                        <small class='text-muted d-block mb-2'>Notater ({item.notes?.length || 0})</small>
                        {item.notes && item.notes.length > 0 && (
                            <div class='mb-2' style='max-height: 200px; overflow-y: auto;'>
                                {item.notes.map(note => (
                                    <div key={note.id} class='border-start border-3 border-info ps-2 mb-2'>
                                        <small class='text-muted'>
                                            {note.userName} - {this.formatDate(note.createdDate)}
                                        </small>
                                        <div style='white-space: pre-wrap;'>{note.body}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {canComplete && (
                            <div class='input-group'>
                                <textarea
                                    class='form-control'
                                    rows='2'
                                    placeholder='Legg til notat...'
                                    value={newNote}
                                    onInput={(e) => this.setState({ newNote: e.target.value })}
                                />
                                <button
                                    class='btn btn-outline-primary'
                                    onClick={this.handleAddNote}
                                    disabled={!newNote.trim()}
                                >
                                    <i class='fa-solid fa-paper-plane' />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Outcome */}
                    {canComplete && (
                        <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                            <label class='form-label text-muted'>Resultat (ved fullføring)</label>
                            <textarea
                                class='form-control'
                                rows='2'
                                placeholder='Beskriv utfallet av jobben...'
                                value={outcome}
                                onInput={(e) => this.setState({ outcome: e.target.value })}
                            />
                        </div>
                    )}

                    {item.status === 'completed' && item.outcome && (
                        <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                            <small class='text-muted'>Resultat</small>
                            <div class='p-2 bg-success bg-opacity-10 rounded' style='white-space: pre-wrap;'>
                                {item.outcome}
                            </div>
                        </div>
                    )}

                    {/* Created by */}
                    <div class='text-center text-muted mt-3' style='font-size: 0.85em;'>
                        Opprettet av: {item.createdByName} - {this.formatDate(item.createdDate)}
                    </div>

                </div>
            </div>

            {/* Action buttons - fixed at bottom */}
            <div class='d-flex flex-wrap justify-content-center gap-2 py-3'>
                {canGrab && (
                    <button class='btn btn-success rounded-pill px-4' onClick={this.handleGrabItem}>
                        <i class='fa-solid fa-hand me-1' /> Ta jobben
                    </button>
                )}
                {canRelease && (
                    <button class='btn btn-warning rounded-pill px-4' onClick={this.handleReleaseItem}>
                        <i class='fa-solid fa-hand-holding me-1' /> Slipp jobben
                    </button>
                )}
                {canComplete && (
                    <button class='btn btn-success rounded-pill px-4' onClick={this.handleCompleteItem}>
                        <i class='fa-solid fa-check me-1' /> Fullfør
                    </button>
                )}
                {canCancel && (
                    <button class='btn btn-outline-danger rounded-pill px-4' onClick={this.handleCancelItem}>
                        <i class='fa-solid fa-times me-1' /> Kanseller
                    </button>
                )}
            </div>
        </>);
    }
}

export default WorkSharingItemDetail;
